'use client';

import React, { useMemo } from 'react';
import { useTranslationDetector } from '@/hooks/useTranslationDetector';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import smoothScrollToElement from '@/utils/smoothScroll';
import { HOTEL_AMENITY_ICON_MAP } from "@/configs/hotel/amenities/iconMap";

// Amenity koduna göre icon mapping (SearchCardItem ile ortak)
const getAmenityIcon = (amenity) => {
    if (!amenity) {
        return byPrefixAndName.far['water-ladder'];
    }

    const code =
        amenity.AmenityCode ??
        amenity.Code ??
        amenity.AmenityId ??
        amenity.Id;

    const iconName = HOTEL_AMENITY_ICON_MAP[code];

    if (iconName) {
        return (
            byPrefixAndName.fak[iconName] ||
            byPrefixAndName.fal[iconName] ||
            byPrefixAndName.far[iconName] ||
            byPrefixAndName.far['water-ladder']
        );
    }

    return byPrefixAndName.far['water-ladder'];
};

export default function HotelFeaturesSection({ hotelData }) {
    const t = useTranslationDetector('hotel');
    // API'den gelen HotelAmenities'i kullan, Highlighted olanları göster
    const features = useMemo(() => {
        const amenities = hotelData?.HotelAmenities || [];

        if (!amenities.length) {
            return [];
        }

        return amenities
            .filter(amenity => amenity.Highlighted !== false)
            .map((amenity, index) => ({
                id: amenity.AmenityId || amenity.AmenityCode || amenity.Code || index,
                name: amenity.AmenityName || '',
                icon: getAmenityIcon(amenity),
            }));
    }, [hotelData]);

    const FEATURE_LIMIT = 6;
    const displayableFeatures = features.slice(0, FEATURE_LIMIT);

    const handleShowAllFeatures = () => {
        const featuresElement = document.getElementById('features');
        if (featuresElement) {
            smoothScrollToElement(featuresElement, 108, 0);
        }
    };

    return (
        <div className='flex flex-col gap-[14px] md:gap-8'>
            <div className='flex flex-col gap-4'>
                <div className='text-base md:text-lg font-semibold text-foreground'>{t('highlightedFeatures')}</div>
            </div>
            <div className='flex flex-col gap-[14px] md:gap-8'>
                <ul className='grid grid-cols-2 md:flex md:flex-wrap items-start justify-between md:justify-start gap-4 md:gap-6'>
                    {displayableFeatures.map((feature) => (
                        <li key={feature.id} className='flex md:flex-col items-center md:justify-center gap-2 min-w-0 md:flex-none md:w-[124px] md:max-w-[124px]'>
                            <span className='md:w-[54px] md:h-[54px] md:bg-background-global md:rounded-full md:flex md:justify-center md:items-center'>
                                <FontAwesomeIcon className='w-5! h-5! lg:w-6! lg:h-6!' icon={feature.icon} />
                            </span>
                            <span className='md:text-center text-foreground font-normal text-sm md:text-base'>{feature.name}</span>
                        </li>
                    ))}
                </ul>
                <button
                    type='button'
                    onClick={handleShowAllFeatures}
                    className='text-primary flex gap-1 items-center cursor-pointer w-fit'
                >
                    <span className='font-medium text-base'>
                        {t('allAmenities')}
                    </span>
                    <FontAwesomeIcon
                        fontSize={16}
                        icon={byPrefixAndName.far['chevron-down']}
                    />
                </button>
            </div>
        </div>
    )
}
